import YouTubeIcon from '@mui/icons-material/YouTube';
import { Box, Button, Stack, Tab, Tabs, tabsClasses, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PostUtil } from 'src/pages/new-post-new/post-util';
import HttpServices from 'src/services/httpService';
import { integrateModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useReAuth } from 'src/utils/reAuth/ReAuthContext';
import Facebook_Icon from '../../assets/facebook.svg';
import Instagram_Icon from '../../assets/instagram.svg';
import Youtube_Icon from '../../assets/youtube.png';
import LinkedIn_Icon from '../../assets/linked_in.svg';
import CommonReAuthenticateComponent from '../integration/commonReAuthenticate';
import FacebookReportComponent from './facebook-report/facebook-report.component';
import InstagramReportComponent from './instagram-report/instagram-report.component';
import YoutubeReportComponent from './youtube-report/youtube-report.component';
import LinkedinReportComponent from './linkedin-report/linkedin-report.component';

// Extend dayjs with the plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const Report = () => {
  const { t: translate } = useTranslation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const cuurentBrandTime = CommonHelper.getBrandTimeZoneTime();
  const { checkAndTriggerReAuth } = useReAuth();
  const navigate = useNavigate();
  const isCustomDomain = CommonHelper.CustomDomain();
  const isPortalAdmin = CommonHelper.GetBrandInfoFromLocalStorage("is_portal_admin");
  //State Variable
  const [currentTab, setCurrentTab] = useState(translate('Facebook Page'));
  const [facebookNetworks, setFacebookNetworks] = useState([]);
  const [instagramNetworks, setInstagramNetworks] = useState([]);
  const [youtubeNetworks, setYoutubeNetworks] = useState([]);
  const [twitterNetworks, setTwitterNetworks] = useState([]);
  const [linkedInNetworks, setLinkedInNetworks] = useState([]);
  const [tiktokNetworks, setTiktokNetworks] = useState([]);
  const [gbusinessNetworks, setGBusinessNetworks] = useState([]);
  const [isNetworksLoaded, setNetworkLoaded] = useState(false);
  const [selectedPopOverAccount, setSelectedPopOverAccount] = useState();
  const [reAuthenticateOpen, setReAuthenticateOpen] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    return cuurentBrandTime.subtract(30, 'days').toDate();
  });
  const [endDate, setEndDate] = useState(() => {
    return cuurentBrandTime.toDate();
  });
  const [viewBy, setViewBy] = useState('day');

  useEffect(() => {
    getConnectedNetworks();
  }, []);

  useEffect(() => {}, [
    facebookNetworks,
    instagramNetworks,
    youtubeNetworks,
    twitterNetworks,
    linkedInNetworks,
    tiktokNetworks,
    gbusinessNetworks,
    isNetworksLoaded,
  ]);
  const connectedNetworksSuccessCallback = (data, message) => {
    const sortByActive = (arr) => [...arr].sort((a, b) => Number(b.is_active) - Number(a.is_active));

    let fNetworks = sortByActive(
      data.filter(
        (a) =>
          a.service_name.toUpperCase() === PostUtil.ServiceName.FACEBOOK &&
          a.profile_type === PostUtil.ProfileType.PAGE &&
          a.is_enabled
      )
    );

    let iNetworks = sortByActive(
      data.filter(
        (a) =>
          a.service_name.toUpperCase() === PostUtil.ServiceName.INSTAGRAM &&
          a.is_enabled &&
          a.profile_type !== 'DIRECT PROFILE'
      )
    );

    let yNetworks = sortByActive(
      data.filter((a) => a.service_name.toUpperCase() === PostUtil.ServiceName.YOUTUBE && a.is_enabled)
    );

    let tNetworks = sortByActive(
      data.filter((a) => a.service_name.toUpperCase() === PostUtil.ServiceName.TWITTER && a.is_enabled)
    );

    let gNetworks = sortByActive(
      data.filter((a) => a.service_name.toUpperCase() === PostUtil.ServiceName.GBUSINESS && a.is_enabled)
    );

    let pNetworks = sortByActive(
      data.filter((a) => a.service_name.toUpperCase() === PostUtil.ServiceName.PINTEREST && a.is_enabled)
    );

    let tiNetworks = sortByActive(
      data.filter((a) => a.service_name.toUpperCase() === PostUtil.ServiceName.TIKTOK && a.is_enabled)
    );

    let lNetworks = sortByActive(
      data.filter(
        (a) =>
          a.service_name.toUpperCase() === PostUtil.ServiceName.LINKEDIN && a.profile_type === 'PAGE' && a.is_enabled
      )
    );
    checkAndTriggerReAuth(data);
    setGBusinessNetworks(gNetworks);
    setFacebookNetworks(fNetworks);
    setInstagramNetworks(iNetworks);
    setYoutubeNetworks(yNetworks);
    setTwitterNetworks(tNetworks);
    setLinkedInNetworks(lNetworks);
    setTiktokNetworks(tiNetworks);
    setNetworkLoaded(true);
  };
  const connectedNetworkFailureCallback = (message) => {
    setFacebookNetworks([]);
    setInstagramNetworks([]);
    setYoutubeNetworks([]);
    setTwitterNetworks([]);
    setLinkedInNetworks([]);
    setTiktokNetworks([]);
    setGBusinessNetworks([]);
  };
  const getConnectedNetworks = () => {
    let getProps = {
      url: integrateModuleURL.getConnectedNetworks,
      successCallback: connectedNetworksSuccessCallback,
      failureCallback: connectedNetworkFailureCallback,
    };
    HttpServices.Get(getProps);
  };

  const handleTabs = (value) => {
    setCurrentTab(value);
  };
  const handleCloseReauthenticate = () => {
    setReAuthenticateOpen(false);
    setSelectedPopOverAccount(null);
  };
  const handleProfileClick = (profile) => {
    setSelectedPopOverAccount(profile);
    setReAuthenticateOpen(true);
  };

  const ACCOUNT_TABS = [
    {
      value: 'Facebook Page',
      label: `${translate('social_inbox.facebook_page')}`,
      icon: <img src={Facebook_Icon} width={28} height={20} />,
      component: (
        <FacebookReportComponent
          connectedNetworks={facebookNetworks}
          startDate={startDate}
          endDate={endDate}
          viewBy={viewBy}
          handleProfileClick={handleProfileClick}
        />
      ),
    },
    {
      value: 'Instagram',
      label: `${translate('social_inbox.instagram')}`,
      icon: <img src={Instagram_Icon} width={28} height={20} />,
      component: (
        <InstagramReportComponent
          connectedNetworks={instagramNetworks}
          startDate={startDate}
          endDate={endDate}
          viewBy={viewBy}
          handleProfileClick={handleProfileClick}
        />
      ),
    },
    {
      value: 'Youtube',
      label: `${translate('report.youtube')}`,
      icon: <img src={Youtube_Icon} width={35} height={35} />,
      component: (
        <YoutubeReportComponent
          connectedNetworks={youtubeNetworks}
          startDate={startDate}
          endDate={endDate}
          viewBy={viewBy}
          handleProfileClick={handleProfileClick}
        />
      ),
    },
    {
      value: 'LinkedIn Page',
      label: translate('published_post.linkedin_page'),
      icon: <img src={LinkedIn_Icon} width={28} height={20} />,
      component: (
        <LinkedinReportComponent
          connectedNetworks={linkedInNetworks}
          startDate={startDate}
          endDate={endDate}
          viewBy={viewBy}
          handleProfileClick={handleProfileClick}
        />
      ),
    },
    // {
    //   value: 'GoogleMyBusiness',
    //   icon: <img src={Gbusiness_Icon} width={28} height={20} />,
    //   component: <>Coming soon</>,
    // },
    // {
    //   value: 'TikTok',
    //   icon: <img src={Tiktok_Icon} width={28} height={20} />,
    //   component: <TikTokPagePostGrid connectedNetworks={tiktokNetworks} />,
    // }
  ];

  return (
    <>
      <Helmet>
        <title> {translate('navbar.menu.social_media_report')} </title>
      </Helmet>
      <Box display="flex" justifyContent="space-between">
        <Tabs
          value={currentTab}
          variant="scrollable"
          sx={{
            [`& .${tabsClasses.scrollButtons}`]: {
              '&.Mui-disabled': { display: 'none' },
            },
            ...(!isMobile && { gap: '0.5rem', minHeight: '37px' }),
          }}
          allowScrollButtonsMobile
          onChange={(e, value) => handleTabs(value)}
        >
          {ACCOUNT_TABS.map((tab) => {
            return (
              <Tab
                disableRipple
                key={tab.value}
                label={isMobile ? '' : tab.label}
                icon={tab.icon}
                value={tab.value}
                sx={{
                  ...(!isMobile && {
                    flexDirection: 'row',
                    gap: '0.25rem',
                    padding: 0,
                    minHeight: '10px',
                    mr: '0.75rem',
                    fontWeight: 600,
                  }),
                }}
              />
            );
          })}
        </Tabs>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          {(!isCustomDomain || isPortalAdmin) && (
            <Button
              variant="outlined"
              startIcon={<YouTubeIcon />}
              onClick={() => window.open('https://www.youtube.com/watch?v=bbDmC5Aa6Lo', '_blank')}
              color="primary"
            >
              {translate('common.tutorial')}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => navigate('/scheduled-report-view')}
            sx={{ minWidth: 120 }}
          >
            View Scheduled Reports
          </Button>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={translate('upcoming_post.start_date')}
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': { fontSize: '0.875rem', padding: '6px 10px' },
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    minWidth: 120,
                  }}
                />
              )}
            />
            <DatePicker
              label={translate('upcoming_post.end_date')}
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': { fontSize: '0.875rem', padding: '6px 10px' },
                    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                    minWidth: 120,
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Stack>
      </Box>
      <Box sx={{ mb: 2 }} />
      {isNetworksLoaded &&
        ACCOUNT_TABS.map((tab) => {
          const isMatched = tab.value === currentTab;
          return isMatched && <Box key={tab.value}>{tab.component}</Box>;
        })}
      {reAuthenticateOpen && (
        <CommonReAuthenticateComponent
          selectedPopOverAccount={selectedPopOverAccount}
          getConnectedNetworks={getConnectedNetworks}
          onClose={handleCloseReauthenticate}
        />
      )}
    </>
  );
};

export default Report;
